/**
 * Copyright (c) 2022-present TVM Engineering Authors.
 *
 */

import axios from "axios";

import { logoutUser } from "./auth";
import types from "../actions/types";
import { API_URL } from "../config";
import { toast_notifications } from "../utils/notifications";
import store from "../store/store";
import history from "../history";
import { actions as CommonActions } from "../reducers/app";

const token = sessionStorage.getItem("token");
// const token = JSON.parse(userFromCookie);

const allowedOrigins =
  "http://localhost:3000,http://localhost:3001,http://motcos.com,http://localhost:8090,http://motcos.tvm-engineering.com";
// const allowedOrigins = process.env.ALLOW_ORIGINS;

// Public auth endpoints answer 401 for wrong credentials, which is not an expired session
const PUBLIC_AUTH_ENDPOINTS = [
  "/auth/signin",
  "/auth/signup",
  "/auth/verification",
  "/auth/checkEmail",
  "/auth/password",
  "/auth/reset-password",
];
const isPublicAuthRequest = (requestConfig) =>
  PUBLIC_AUTH_ENDPOINTS.some((endpoint) => (requestConfig?.url || "").includes(endpoint));

// options() runs on every request, so register the interceptors only once
let interceptorsRegistered = false;

export const options = (url, data, method = "post", params = "") => {
  // axios.defaults.baseURL = 'http://h2960355.stratoserver.net:8090';
  if (token) {
    axios.defaults.headers.common["Authorization"] = token;
  }
  axios.defaults.headers.post["Content-Type"] = "application/json";
  axios.defaults.headers.post["Access-Control-Allow-Origin"] = allowedOrigins;
  axios.defaults.headers.post["X-Content-Type-Options"] = "nosniff";
  if (!interceptorsRegistered) {
    interceptorsRegistered = true;

    axios.interceptors.request.use(
      (request) => {
        // log a message before any HTTP request is sent
        // console.log(request);
        // console.log('Axios::Interceptors::Request was sent');
        // Edit request config
        return request;
      },
      (error) => {
        // console.log(error);
        return Promise.reject(error);
      }
    );

    axios.interceptors.response.use(
      (response) => response,
      (error) => {
        if (
          error.response &&
          error.response.status === 401 &&
          !isPublicAuthRequest(error.config)
        ) {
          sessionStorage.clear();
          history.push("/");
          history.replace("/");
          history.go("/");
          store.dispatch(CommonActions.goOffline());
          toast_notifications.info("Your session have expired");
        }
        return Promise.reject(error);
      }
    );
  }

  if (method === "get") {
    return {
      url: url,
      headers: {
        "Content-Type": "application/json",
        "Cache-Control": "no-cache",
        Accept: "application/json",
      },
      method: method,
      params: params,
      credentials: "same-origin", // include, same-origin, *omit
      cache: "no-cache", // *default, no-cache, reload, force-cache, only-if-cached
      withCredentials: true,
      withAuthentication: true,
    };
  } else {
    return {
      url: url,
      headers: {
        "Content-Type": "application/json",
        Accept: "application/json",
        "Cache-Control": "no-cache",
      },
      method: method,
      data: data !== {} ? data : JSON.stringify(data),
      credentials: "same-origin", // include, same-origin, *omit
      withCredentials: true,
      withAuthentication: true,
    };
  }
};

export const config = (body, method = "POST") => {
  if (method === "GET") {
    return {
      headers: {
        "Content-Type": "application/json",
        Accept: "application/json",
        "Cache-Control": "no-cache",
      },
      method: method,
      cache: "no-cache", // *default, no-cache, reload, force-cache, only-if-cached
      credentials: "same-origin", // include, same-origin, *omit
      withCredentials: true,
      withAuthentication: true,
    };
  } else {
    return {
      headers: {
        "Content-Type": "application/json",
        Accept: "application/json",
        "Cache-Control": "no-cache",
      },
      method: method,
      cache: "no-cache", // *default, no-cache, reload, force-cache, only-if-cached
      credentials: "same-origin", // include, same-origin, *omit
      body: body !== {} ? JSON.stringify(body) : "",
      withCredentials: true,
      withAuthentication: true,
    };
  }
};

export const downloadOptions = (url, data, method = "get", params = "", contentType) => {
  if (token) {
    axios.defaults.headers.common["Authorization"] = token;
  }
  axios.defaults.headers.post["Content-Type"] = contentType;
  axios.defaults.headers.post["Access-Control-Allow-Origin"] = allowedOrigins;
  axios.defaults.headers.post["X-Content-Type-Options"] = "nosniff";
  // axios.defaults.headers.respnseType = 'arraybuffer';

  axios.interceptors.request.use(
    (request) => {
      return request;
    },
    (error) => {
      return Promise.reject(error);
    }
  );

  return {
    url: url,
    headers: {
      "Content-Type": contentType,
      "Cache-Control": "no-cache",
      Accept: contentType,
    },
    method: method,
    responseType: "blob",
    credentials: "same-origin", // include, same-origin, *omit
    withCredentials: true,
    withAuthentication: true,
  };
};

export function fetchUserMe(method, body) {
  return async (dispatch) => {
    try {
      const response = await axios(options(API_URL + "/user", body, method));
      const responseOK = response && response.status === 200 && response.statusText === "OK";
      if (responseOK) {
        // cookie.save('sessionID', json.user.sessionID, { path: '/profile' });
        // cookie.save('user', json, { path: '/profile' });
        dispatch({
          type: types.FETCH_USER,
          payload: response.data, // json.user,
        });
        if (method === "PUT") {
          if (body.password.length > 0) {
            fetchUpdatePassword(
              "POST",
              {
                pass: body.password,
                confirmPassword: body.confirmPassword,
              },
              dispatch
            );
          }
          toast_notifications.success("Successfully updated profile!");
        }
      }
    } catch (error) {
      // console.warn('Error:', error);
      dispatch(errorHandler(error));
    }
  };
}

export function fetchLanguage(method, body) {
  return async (dispatch) => {
    try {
      const response = await fetch(API_URL + "/users/me/language", config(body, method));
      const json = await response.json();
      if (!response.ok) {
        response.error = json.error;
        throw response;
      }
      // TODO: This might need to be reworked

      // const user = cookie.load("user");
      // user.settings = json.settings;
      // cookie.save("user", user, { path: "/" });
    } catch (error) {
      dispatch(errorHandler(error));
    }
  };
}

export function fetchUpdatePassword(method, body) {
  return async (dispatch) => {
    try {
      const response = await axios(options(API_URL + "/admin/password", body, method));
      const responseOK = response && response.status === 200 && response.statusText === "OK";
      if (responseOK) {
        dispatch({
          type: types.FETCH_UPDATE_PASSWORD,
          payload: response.data, // json.user,
        });
        toast_notifications.success("Successfully updated password!");
      }
    } catch (error) {
      dispatch(errorHandler(error));
    }
  };
}

export function fetchSendContact(body) {
  return async (dispatch) => {
    try {
      const response = await fetch(API_URL + "/contact", config(body));
      const json = await response.json();
      if (!response.ok) {
        response.error = json.error;
        throw response;
      }
      dispatch({
        type: types.SEND_CONTACT_FORM,
        payload: json, // json.user,
      });
    } catch (error) {
      dispatch(errorHandler(error));
    }
  };
}

export function errorHandler(error, type = "UNAUTH_USER") {
  return (dispatch) => {
    let errorMessage = error.error ? error.error : error.data.error;
    if (error.status === 401 || (error.response && error.response.status === 401)) {
      if (errorMessage === null || errorMessage === undefined) {
        errorMessage = "Wrong username or password. You are not authorized.";
      }

      toast_notifications.warning(errorMessage);
    }
    if (error.status === 422 || (error.response && error.response.status === 422)) {
      if (errorMessage === null || errorMessage === undefined) {
        errorMessage = "Wrong username or password. You are not authorized.";
      }

      toast_notifications.warning(errorMessage);
    }

    // NOT AUTHENTICATED ERROR
    if (error.status === 403 || (error.response && error.response.status === 403)) {
      if (errorMessage === null || errorMessage === undefined) {
        errorMessage =
          "The server encountered a temporary error and could not complete your request. Please try again in 30 seconds.";
      }

      toast_notifications.warning(errorMessage);
      return dispatch(logoutUser(errorMessage));
    }

    if (error.status === 504 || (error.response && error.response.status === 504)) {
      if (errorMessage === null || errorMessage === undefined) {
        errorMessage =
          "The server encountered a temporary error and could not complete your request. Please try again in 30 seconds.";
      }

      toast_notifications.warning(
        "The server encountered a temporary error and could not complete your request. Please try again in 30 seconds."
      );
    }

    dispatch({
      type,
      payload: errorMessage,
    });
  };
}
